import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowRight, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const leadIdFromUrl = params.get("lead_id");
  // Treat the user as paid as soon as Stripe redirects them here. The status
  // poll runs in the background to enrich the page with paid amount/plan.
  const [data, setData] = useState({
    lead_id: leadIdFromUrl || null,
    plan_name: null,
    amount_total: null,
    currency: "nzd",
  });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 6;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data: d } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData((prev) => ({
          lead_id: prev.lead_id || d.lead_id || null,
          plan_name: d.plan_name || prev.plan_name,
          amount_total: d.amount_total ?? prev.amount_total,
          currency: d.currency || prev.currency,
        }));
        if (d.payment_status === "paid") return;
        if (attempts >= maxAttempts) return;
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        // Silent — status poll is best-effort. We still proceed with onboarding
        // because Stripe redirected here, which means the payment went through.
        if (attempts >= maxAttempts) return;
        attempts += 1;
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const leadId = data.lead_id;

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6"
      data-testid="checkout-success-page"
    >
      <div className="max-w-xl w-full bg-white border border-slate-200 p-8 md:p-12">
        <div className="flex flex-col items-start gap-4" data-testid="checkout-status-paid">
          <CheckCircle2 className="w-10 h-10 text-[#C9A349]" />
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">
            Payment received
          </p>
          <h1 className="text-4xl font-black tracking-tight">You're paid in.</h1>
          <p className="text-slate-600">
            Welcome to NZ Taxation Limited
            {data.plan_name ? ` — ${data.plan_name}` : ""}. One last step: please complete your
            onboarding form so we can prepare your first monthly tax position email and connect
            your records.
          </p>

          {data.amount_total != null && (
            <div className="w-full mt-2 border-t border-slate-200 pt-4 text-sm text-slate-600">
              Paid:{" "}
              <strong>
                ${data.amount_total.toFixed(2)} {(data.currency || "NZD").toUpperCase()}
              </strong>
            </div>
          )}
          {data.amount_total == null && (
            <div className="w-full mt-2 border-t border-slate-200 pt-4 flex items-center gap-2 text-xs text-slate-500">
              <Clock className="w-3 h-3" /> Receipt details syncing in the background…
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-2">
            {leadId ? (
              <Link
                to={`/onboarding?lead_id=${leadId}`}
                className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-medium hover:bg-[#C9A349] transition-colors"
                data-testid="success-go-onboarding"
              >
                Continue to onboarding <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <span
                className="inline-flex items-center gap-2 bg-slate-200 text-slate-500 px-6 py-3 font-medium cursor-not-allowed"
                data-testid="success-go-onboarding-disabled"
              >
                Preparing your onboarding link… <Clock className="w-4 h-4" />
              </span>
            )}
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-3 font-medium text-slate-700 hover:border-[#C9A349] hover:text-[#C9A349] transition-colors"
              data-testid="success-back-home"
            >
              Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
